import { z } from 'zod';

/**
 * Login form validation schema
 */
export const loginSchema = z.object({
  email: z
    .string()
    .min(1, 'Email is required.')
    .email('Please enter a valid email address.')
    .max(255, 'Email is too long.'),
  password: z
    .string()
    .min(1, 'Password is required.')
    .min(8, 'Password must be at least 8 characters.')
    .max(128, 'Password is too long.'),
});

export type LoginFormData = z.infer<typeof loginSchema>;

// The Cognito pool's policy: every rule a new password breaks is named in one message.
const PASSWORD_INCLUDES: [RegExp, string][] = [
  [/[a-z]/, 'a lowercase letter'],
  [/[A-Z]/, 'an uppercase letter'],
  [/[0-9]/, 'a number'],
  [/[^a-zA-Z0-9]/, 'a symbol'],
];

function joinList(items: string[]): string {
  return items.length > 1 ? `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}` : (items[0] ?? '');
}

/** What a new password still needs, as one sentence, or null when it meets the policy. */
export function passwordRequirementsMessage(password: string): string | null {
  const tooShort = password.length < 8;
  const missing = PASSWORD_INCLUDES.filter(([pattern]) => !pattern.test(password)).map(([, needs]) => needs);
  if (!tooShort && missing.length === 0) return null;
  if (missing.length === 0) return 'Password must be at least 8 characters.';
  return tooShort
    ? `Password must be at least 8 characters and include ${joinList(missing)}.`
    : `Password must include ${joinList(missing)}.`;
}

const newPassword = z.string().superRefine((password, context) => {
  const message = passwordRequirementsMessage(password);
  if (message) context.addIssue({ code: 'custom', message });
});

const verificationCode = z
  .string()
  .min(6, 'Enter the 6-digit code.')
  .max(6, 'Enter the 6-digit code.')
  .regex(/^\d{6}$/, 'Enter a valid 6-digit verification code.');

export const signupSchema = z
  .object({
    email: z.string().min(1, 'Email is required.').email('Please enter a valid email address.'),
    password: newPassword,
    confirmPassword: z.string().min(1, 'Please confirm your password.'),
    acceptTerms: z.boolean().refine((v) => v === true, { message: 'You must accept the terms.' }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match.',
    path: ['confirmPassword'],
  });

export type SignupFormData = z.infer<typeof signupSchema>;

export const verifyEmailSchema = z.object({
  code: verificationCode,
});

export type VerifyEmailFormData = z.infer<typeof verifyEmailSchema>;

export const forgotPasswordSchema = z.object({
  email: z.string().min(1, 'Email is required.').email('Please enter a valid email address.'),
});

export type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

export const resetPasswordSchema = z
  .object({
    code: verificationCode,
    password: newPassword,
    confirmPassword: z.string().min(1, 'Please confirm your password.'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match.',
    path: ['confirmPassword'],
  });

export type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;

/** One message per field: the first rule it fails, in the order the schema lists them. */
export function firstFieldErrors<T>(error: z.ZodError<T>): Partial<Record<keyof T, string>> {
  const errors: Partial<Record<keyof T, string>> = {};
  for (const issue of error.issues) {
    const field = issue.path[0] as keyof T | undefined;
    if (field !== undefined && errors[field] === undefined) errors[field] = issue.message;
  }
  return errors;
}

/** Cognito rejected the code itself, so its message belongs on the code field. */
export function isVerificationCodeError(error: unknown): boolean {
  const code = (error as { code?: string; name?: string })?.code || (error as { code?: string; name?: string })?.name;
  return code === 'CodeMismatchException' || code === 'ExpiredCodeException';
}

const PENDING_SIGNUP_EMAIL_KEY = 'pendingSignupEmail';
const PENDING_SIGNUP_PASSWORD_KEY = 'pendingSignupPassword';

export function storePendingSignup(email: string, password: string): void {
  if (typeof sessionStorage === 'undefined') return;
  sessionStorage.setItem(PENDING_SIGNUP_EMAIL_KEY, email.toLowerCase());
  sessionStorage.setItem(PENDING_SIGNUP_PASSWORD_KEY, password);
}

export function getPendingSignup(): { email: string; password: string } | null {
  if (typeof sessionStorage === 'undefined') return null;
  const email = sessionStorage.getItem(PENDING_SIGNUP_EMAIL_KEY);
  const password = sessionStorage.getItem(PENDING_SIGNUP_PASSWORD_KEY);
  if (!email || !password) return null;
  return { email, password };
}

export function clearPendingSignup(): void {
  if (typeof sessionStorage === 'undefined') return;
  sessionStorage.removeItem(PENDING_SIGNUP_EMAIL_KEY);
  sessionStorage.removeItem(PENDING_SIGNUP_PASSWORD_KEY);
}

// The email a reset code was sent to, carried from /forgot-password to
// /reset-password. Only the address is stored — never the password.
const PENDING_RESET_EMAIL_KEY = 'pendingResetEmail';

export function storePendingResetEmail(email: string): void {
  if (typeof sessionStorage === 'undefined') return;
  sessionStorage.setItem(PENDING_RESET_EMAIL_KEY, email.toLowerCase());
}

export function getPendingResetEmail(): string | null {
  if (typeof sessionStorage === 'undefined') return null;
  return sessionStorage.getItem(PENDING_RESET_EMAIL_KEY);
}

export function clearPendingResetEmail(): void {
  if (typeof sessionStorage === 'undefined') return;
  sessionStorage.removeItem(PENDING_RESET_EMAIL_KEY);
}

/**
 * Map Cognito error codes to user-friendly messages. `fallback` names the step that failed.
 */
export function getCognitoErrorMessage(
  error: unknown,
  fallback = 'An error occurred during sign in. Please try again.'
): string {
  const code = (error as { code?: string; name?: string })?.code || (error as { code?: string; name?: string })?.name;

  switch (code) {
    case 'UserNotFoundException':
    case 'NotAuthorizedException':
      return 'Invalid email or password. Please try again.';
    case 'UserNotConfirmedException':
      return 'Please confirm your email address before signing in.';
    case 'PasswordResetRequiredException':
      return 'Password reset required. Please reset your password.';
    case 'TooManyRequestsException':
    case 'LimitExceededException':
      return 'Too many attempts. Please try again later.';
    case 'InvalidParameterException':
      return 'Invalid input. Please check your credentials.';
    case 'UsernameExistsException':
      return 'This email is already registered. Try signing in instead.';
    case 'CodeMismatchException':
      return 'The verification code is incorrect. Please try again.';
    case 'ExpiredCodeException':
      return 'The verification code has expired. Request a new code.';
    case 'NetworkError':
      return 'Network error. Please check your connection and try again.';
    default:
      return fallback;
  }
}

/**
 * Map Cognito errors raised during password reset to user-friendly messages.
 * `getCognitoErrorMessage` falls back to sign-in wording, which is wrong here.
 * Messages match the mobile app so both clients read the same.
 */
export function getPasswordResetErrorMessage(error: unknown, stage: 'request' | 'confirm'): string {
  const code = (error as { code?: string; name?: string })?.code || (error as { code?: string; name?: string })?.name;

  switch (code) {
    case 'LimitExceededException':
    case 'TooManyRequestsException':
    case 'TooManyFailedAttemptsException':
      return 'Too many attempts. Please try again later.';
    case 'UserNotFoundException':
      return 'No account found with this email address.';
    case 'CodeMismatchException':
      return 'Invalid verification code. Please try again.';
    case 'ExpiredCodeException':
      return 'Verification code has expired. Please request a new code.';
    case 'CodeDeliveryFailureException':
      return 'We could not send the code to that email address. Please try again later.';
    case 'InvalidPasswordException':
      return 'Password must be at least 8 characters and include uppercase, lowercase, numbers, and special characters.';
    case 'InvalidParameterException':
      return stage === 'request'
        ? 'Please enter a valid email address.'
        : 'Please check the code and password and try again.';
    case 'NotAuthorizedException':
      // Reset is unavailable for unconfirmed or disabled accounts.
      return 'This account cannot be reset right now. Please contact support.';
    case 'NetworkError':
      return 'Network error. Please check your connection and try again.';
    default:
      return stage === 'request'
        ? 'Failed to request password reset. Please try again.'
        : 'Failed to reset password. Please try again.';
  }
}
